import React, { useEffect, useRef, useState } from 'react';
import { Mail, ArrowRight, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
export function RecoverPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // When the view swaps (form ⇄ confirmation) the page height changes a lot,
  // so return to the top where the new heading sits below the fixed header.
  // Skipped on first render so a normal page load is untouched.
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
  }, [isSubmitted]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
    }, 1500);
  };
  return <div className="min-h-screen bg-gray-50 pt-40 lg:pt-52 pb-20" data-id="element-1500" data-mp="28f3xu4nsye92">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1501" data-mp="24cr46emby6sn">
        {!isSubmitted ? <>
            {/* Page Header */}
            <div className="text-center mb-8" data-id="element-1502" data-mp="2i8ujl1sjoal2">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-purple-100 rounded-full mb-4" data-id="element-1503" data-mp="2db2sg9zq4shx">
                <Lock className="w-8 h-8 text-brand-purple-600" data-id="element-1504" data-mp="2rac7358a7ejs" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1505" data-mp="2n98q2fehzcgz">
                Forgot Your Password?
              </h1>
              <p className="text-lg text-gray-600" data-id="element-1506" data-mp="310x1eggx9www">
                No problem!
                <br data-id="element-1507" data-mp="2w3x0ejyxjkcn" />
                Enter your email address and we'll send you a link to reset
                your password.
              </p>
            </div>

            {/* Help Tips */}
            <Card className="p-6 mb-8 bg-blue-50 border-blue-200" data-id="element-1508" data-mp="3622wum1yvya4">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center" data-id="element-1509" data-mp="3d4hvqyct078k">
                <AlertCircle className="w-5 h-5 mr-2" data-id="element-1510" data-mp="3i32nsahwjklx" />
                Password Not Working? Here Are a Few Suggestions:
              </h3>
              <ul className="space-y-2 text-sm text-blue-800" data-id="element-1511" data-mp="3n14e9ghnqm7f">
                <li className="flex items-start" data-id="element-1512" data-mp="3rsm5vdxsjafq">
                  <span className="font-semibold mr-2" data-id="element-1513" data-mp="3rugq0q18myir">1.</span>
                  <span data-id="element-1514" data-mp="n4cckzkd37l">
                    Look at your original registration email titled "Welcome to
                    Desert Sage Seminars". That email contains your username.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1515" data-mp="9okhad7m0d4f">
                  <span className="font-semibold mr-2" data-id="element-1516" data-mp="em3xhinvazz0">2.</span>
                  <span data-id="element-1517" data-mp="elkpourxlryp">
                    Your browser may be saving an incorrect password. Try
                    clearing the password box and re-type the correct password.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1518" data-mp="28u7r28xrqz3d">
                  <span className="font-semibold mr-2" data-id="element-1519" data-mp="28uzolpaxf6wa">3.</span>
                  <span data-id="element-1520" data-mp="243rcumm4j9fy">
                    BEFORE resetting your password, add
                    Administration@DesertSageSeminars.com to your Safe Senders
                    list so that you can receive our emails.
                  </span>
                </li>
              </ul>
            </Card>

            {/* Reset Form */}
            <Card className="p-8" data-id="element-1521" data-mp="2rrr3znncl1v2">
              <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1522" data-mp="2rqzfews644ea">
                Reset Your Password
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1523" data-mp="2humfvi9xywbm">
                <div data-id="element-1524" data-mp="2hwz6ejtrmf0a">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1525" data-mp="2cvtbcfcmufnl">
                    Email Address<span className="text-red-500" data-id="element-1526" data-mp="16dmnkmnlghhl">*</span>
                  </label>
                  <div className="relative" data-id="element-1527" data-mp="11f320ncwx7pr">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1528" data-mp="2qb3m1zld87f1">
                      <Mail className="h-5 w-5 text-gray-400" data-id="element-1529" data-mp="2v22351e6h9jc" />
                    </div>
                    <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="Enter your email address" required data-id="element-1530" data-mp="2gcw69k2tbvem" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-1531" data-mp="341yl6k62ba5f">
                    Enter the email address associated with your account
                  </p>
                </div>

                <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1532" data-mp="1nqbgup5wdtdp">
                  Send Reset Link <ArrowRight className="ml-2 h-4 w-4" data-id="element-1533" data-mp="5qc5g65pet19" />
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-200 text-center" data-id="element-1534" data-mp="jq4orgewi6z7">
                <p className="text-sm text-gray-600" data-id="element-1535" data-mp="jo0mt40kbi1k">
                  Remember your password?{' '}
                  <a href="/login-register" className="font-medium text-brand-purple-600 hover:text-brand-purple-700 hover:underline" data-id="element-1536" data-mp="fmowew9v6ssb">
                    Back to Login
                  </a>
                </p>
              </div>
            </Card>

            {/* Support Section */}
            <div className="mt-8 text-center" data-id="element-1537" data-mp="3s5oew6k9z9a1">
              <p className="text-sm text-gray-600 mb-2" data-id="element-1538" data-mp="3s3ttkr53df5f">
                Still having trouble?
              </p>
              <a href="/Contact" className="text-sm font-medium text-brand-purple-600 hover:text-brand-purple-700 hover:underline" data-id="element-1539" data-mp="34dzjghc5q4ov">
                Contact our support team
              </a>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1540" data-mp="18cfm6q7g95c6">
                or call us at{' '}
                <a href="tel:602-377-6207" className="font-semibold text-gray-700 hover:text-brand-purple-600" data-id="element-1541" data-mp="1d3nwrpkimwqx">
                  602-377-6207
                </a>
              </p>
            </div>
          </> /* Success Message */ : <Card className="p-8 text-center" data-id="element-1542" data-mp="pdtntj3bhsby">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" data-id="element-1543" data-mp="pelh2n9fzdz2">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-1544" data-mp="ufivqpv8tpvt" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1545" data-mp="zdu7j653mago">
              Check Your Email
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1546" data-mp="25upjqcug9wl0">
              If an account exists for this email address, you will receive a
              password reset link.
            </p>
            <p className="text-lg font-semibold text-brand-purple-600 mb-6 break-all" data-id="element-1547" data-mp="3v8xfc0o388u7">
              {email}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left" data-id="element-1548" data-mp="3qd01q247lgl4">
              <h3 className="font-semibold text-blue-900 mb-2" data-id="element-1549" data-mp="3lhkpfelunsqt">Next Steps:</h3>
              <ol className="space-y-2 text-sm text-blue-800" data-id="element-1550" data-mp="3gkki70d9kjy4">
                <li className="flex items-start" data-id="element-1551" data-mp="3bmsqa6cmcxtx">
                  <span className="font-semibold mr-2" data-id="element-1552" data-mp="3bjenix01mmly">1.</span>
                  <span data-id="element-1553" data-mp="37jcxjl3o7hmi">
                    Check your email inbox for a message from Desert Sage
                    Seminars
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1554" data-mp="2xmrpeqwctqeg">
                  <span className="font-semibold mr-2" data-id="element-1555" data-mp="2xo2ucu1jd475">2.</span>
                  <span data-id="element-1556" data-mp="10sn3g4nczqkp">Click the password reset link in the email</span>
                </li>
                <li className="flex items-start" data-id="element-1557" data-mp="vvmzbza2kxw8">
                  <span className="font-semibold mr-2" data-id="element-1558" data-mp="vssh8eej3p5z">3.</span>
                  <span data-id="element-1559" data-mp="4qkfx96rlk04">Create a new password for your account</span>
                </li>
              </ol>
            </div>
            <p className="text-sm text-gray-600 mb-6" data-id="element-1560" data-mp="9qf2j0jibnfd">
              Didn't receive the email? Check your spam folder or{' '}
              <button onClick={() => setIsSubmitted(false)} className="font-medium text-brand-purple-600 hover:text-brand-purple-700 hover:underline" data-id="element-1561" data-mp="3w3ndwzmv1yvs">
                try again
              </button>
            </p>
            <Button onClick={() => window.location.href = '/login-register'} variant="outline" size="lg" data-id="element-1562" data-mp="3n4jws1797tjx">
              Back to Login
            </Button>
          </Card>}
      </div>
    </div>;
}